import {
  GetKitCombinationCount,
  GetKitCombinationsPageParams,
} from '../../../../services/modules/kit/kitDetails/kitDetails.types';
import { getOrderProductUniqueIdentifier } from '../../../../utils/getOrderProductUniqueIdentifier';
import { OrderProduct } from '../../../orders/products/orderProducts.types';
import {
  ClosedKitCombination,
  KitCombinationTypes,
  KitItemCombination,
} from '../../../products/products-details-v2/types/kit-combinations.types';
import {
  OfflineDatabaseKit,
  OfflineDatabaseSkuKit,
} from '../../database/offlineDatabase/types/offlineDatabaseKit.types';
import { offlineDeliveryWindowsRepository } from '../../repositories/offlineDeliveryWindows.repository';
import { offlineOrdersRepository } from '../../repositories/offlineOrders.repository';
import { offlineProductColorsRepository } from '../../repositories/offlineProductColors.repository';
import { offlineProductsRepository } from '../../repositories/offlineProducts.repository';
import { MountClosedKitCombinationParams } from '../types/kitDetailsQueriesUtils.types';

export class KitDetailsQueriesUtils {
  async getCombinations(kit: OfflineDatabaseKit, params: GetKitCombinationsPageParams) {
    const closedKitCombinations = await this.getClosedKitCombinationsFromOrders(kit, params);
    const kitItemCombinations = await this.getKitItemCombinations(kit, params);

    const allCombinations: (ClosedKitCombination | KitItemCombination)[] = [
      ...closedKitCombinations,
      ...kitItemCombinations,
    ];

    return allCombinations;
  }

  private async getKitItemCombinations(
    kit: OfflineDatabaseKit,
    params: GetKitCombinationsPageParams,
  ) {
    const kitItemCombinations: KitItemCombination[] = [];
    const combinationSet = new Set<string>();

    await Promise.all(
      kit.skuKits.map(async (skuKit) => {
        if (
          params.filters?.availabilities &&
          !params.filters.availabilities.includes(skuKit.availability)
        ) {
          return;
        }

        const combinationKey = this.mountKitItemCombinationKey(skuKit);
        if (combinationSet.has(combinationKey)) {
          return;
        }
        combinationSet.add(combinationKey);

        const deliveryWindow = skuKit.deliveryWindow?.id
          ? await offlineDeliveryWindowsRepository.getById(skuKit.deliveryWindow.id)
          : null;
        const productColor = await offlineProductColorsRepository.getById(skuKit.productColor.id);
        const product = await offlineProductsRepository.getById(skuKit.product.id);

        if (!productColor || !product) {
          console.error('produto do kit não encontrado para montar combinações de kit', {
            productColorId: skuKit.productColor.id,
            productId: skuKit.product.id,
          });
          throw new Error('produto do kit não encontrado para montar combinações de kit');
        }

        const combination: KitItemCombination = {
          type: KitCombinationTypes.KIT_ITEM,
          availability: skuKit.availability,
          deliveryWindowId: deliveryWindow?.id || null,
          deliveryWindowCode: deliveryWindow?.code || null,
          deliveryWindowName: deliveryWindow?.name || null,
          deliveryWindowStartDate: deliveryWindow?.startDate?.toString() || null,
          deliveryWindowEndDate: deliveryWindow?.endDate?.toString() || null,
          kitId: kit.id,
          kitName: kit.name,
          kitCode: kit.code,
          kitImageUrl: kit.imageUrl || null,
          productColorId: productColor.id,
          productColorCode: productColor.code,
          productColorName: productColor.name,
          productId: product.id,
          productName: product.name,
          productCode: product.code,
          productReferenceCode: product.referenceCode || null,
          allowChangeImmediateGrid: productColor.allowChangeImmediateGrid,
          allowChangePreOrderGrid: productColor.allowChangePreOrderGrid,
          mediaUrl: productColor.medias[0]?.url || null,
          productColorHexCode: productColor.hexcode || null,
          productColorImageUrl: productColor.imageUrl || null,
          translationId: null,
          translationValue: null,
          translationLanguage: null,
        };

        kitItemCombinations.push(combination);
      }),
    );

    return kitItemCombinations;
  }

  private async getClosedKitCombinationsFromOrders(
    kit: OfflineDatabaseKit,
    params: GetKitCombinationsPageParams,
  ) {
    if (!params.filters?.orderIds || params.filters.orderIds.length === 0) {
      const combinationWithoutOrder = this.mountClosedKitCombination({
        kit,
        kitOrderQuantity: null,
      });

      return [combinationWithoutOrder];
    }

    const closedKitCombinations = await Promise.all(
      params.filters?.orderIds?.map(async (orderId) => {
        const order = await offlineOrdersRepository.getById(orderId);
        if (!order) {
          throw new Error(`pedido ${orderId} não encontrado para montar combinações de kit`);
        }

        const kitOrderProducts = order.products?.filter((product) => product.kit?.id === kit.id);
        if (!kitOrderProducts || kitOrderProducts.length === 0) {
          const combination: ClosedKitCombination = this.mountClosedKitCombination({
            order,
            kit,
            kitOrderQuantity: null,
          });

          return combination;
        }

        const skuKitToUseOnCalculation =
          kit.skuKits.find((skuKit) => skuKit.quantity > 0) || kit.skuKits[0];
        const orderProductFromSkuKit = kitOrderProducts.find(
          (orderProduct) =>
            orderProduct.productColor.id === skuKitToUseOnCalculation.productColor.id,
        )!;
        const orderProductGridItemQuantity =
          orderProductFromSkuKit.grid.find(
            (gridItem) => gridItem.sku.id === skuKitToUseOnCalculation.sku.id,
          )?.quantity || 0;
        const kitOrderQuantity =
          Math.floor(orderProductGridItemQuantity / skuKitToUseOnCalculation.quantity) || null;

        const combination: ClosedKitCombination = this.mountClosedKitCombination({
          order,
          kit,
          kitOrderQuantity,
        });

        return combination;
      }) ?? [],
    );

    return closedKitCombinations;
  }

  private mountClosedKitCombination({
    order,
    kit,
    kitOrderQuantity,
  }: MountClosedKitCombinationParams): ClosedKitCombination {
    return {
      type: KitCombinationTypes.CLOSED_KIT,
      kitId: kit.id,
      kitCode: kit.code,
      kitName: kit.name,
      kitImageUrl: kit.imageUrl,
      kitWholesalePrice: kit.wholesalePrice,
      kitAvailableAmount: kit.availableAmount,
      customerCommercialName: order?.customer?.commercialName || null,
      customerId: order?.customer?.id || null,
      orderId: order?.id || null,
      orderCode: order ? String(order?.code) : null,
      orderQuantity: kitOrderQuantity,
    };
  }

  async getCombinationsCount(kit: OfflineDatabaseKit, params: GetKitCombinationsPageParams) {
    const itemsCombinationCount = this.mountKitItemsCombinationCount(kit, params);

    if (!params.filters?.orderIds || params.filters.orderIds.length === 0) {
      return [this.mountKitCombinationCountWithoutOrder(), itemsCombinationCount];
    }

    const orderCombinationCount = await Promise.all(
      params.filters?.orderIds.map((orderId) => this.mountKitCombinationCount(kit, orderId)),
    );

    return [...orderCombinationCount, itemsCombinationCount];
  }

  private mountKitCombinationCountWithoutOrder(): GetKitCombinationCount {
    return {
      count: 1,
      type: KitCombinationTypes.CLOSED_KIT,
      orderId: null,
      subTotal: null,
      totalItems: null,
      totalPieces: null,
    };
  }

  private async mountKitCombinationCount(
    kit: OfflineDatabaseKit,
    orderId: string,
  ): Promise<GetKitCombinationCount> {
    const order = await offlineOrdersRepository.getById(orderId);

    if (!order) {
      return {
        count: 1,
        type: KitCombinationTypes.CLOSED_KIT,
        orderId: null,
        subTotal: null,
        totalItems: null,
        totalPieces: null,
      };
    }

    const orderProductsFromKit = (order.products || []).filter(
      (product) => product.kit?.id === kit.id,
    );
    const totalItems = orderProductsFromKit.length;
    const { totalPieces, subTotal } = orderProductsFromKit.reduce(
      (acc, product) => {
        const productQuantity = product.grid.reduce((sum, item) => sum + (item.quantity || 0), 0);

        acc.totalPieces += productQuantity;
        acc.subTotal += (product.subTotal || 0) * productQuantity;

        return acc;
      },
      { totalPieces: 0, subTotal: 0 },
    );

    return {
      count: 1,
      type: KitCombinationTypes.CLOSED_KIT,
      orderId,
      subTotal,
      totalItems,
      totalPieces,
    };
  }

  private mountKitItemsCombinationCount(
    kit: OfflineDatabaseKit,
    params: GetKitCombinationsPageParams,
  ): GetKitCombinationCount {
    const combinationSet = new Set<string>();

    kit.skuKits.forEach((skuKit) => {
      if (
        params.filters?.availabilities &&
        !params.filters.availabilities.includes(skuKit.availability)
      ) {
        return;
      }

      const combinationString = getOrderProductUniqueIdentifier({
        productColorId: skuKit.productColor.id,
        availability: skuKit.availability,
        deliveryWindow: { id: skuKit.deliveryWindow?.id } as OrderProduct['deliveryWindow'],
      });
      combinationSet.add(combinationString);
    });

    const totalCombinations = combinationSet.size;

    return {
      count: totalCombinations,
      type: KitCombinationTypes.KIT_ITEM,
      orderId: null,
      subTotal: null,
      totalItems: null,
      totalPieces: null,
    };
  }

  private mountKitItemCombinationKey(skuKit: OfflineDatabaseSkuKit) {
    return getOrderProductUniqueIdentifier({
      productColorId: skuKit.productColor.id,
      availability: skuKit.availability,
      deliveryWindow: { id: skuKit.deliveryWindow?.id } as OrderProduct['deliveryWindow'],
    });
  }
}
